"use client";

import useContentAutoResume from "@/hooks/useContentAutoResume";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Button } from "@nextui-org/react";
import CustomButton from "../common/button";
import WatchPlayIcon from "@/helpers/svg/watchPlayIcon.svg";
import ClockIcon from "@/helpers/svg/clockIcon.svg";
import { ROUTES } from "@/routes/appRoutes";
import ImdbIcon from "@/helpers/svg/imdbIcon.svg";
import ThumbsDown from "@/helpers/svg/thumbsDown.svg";
import ThumbsDownFilled from "@/helpers/svg/thumbsDownFilled.svg";
import ThumbsUp from "@/helpers/svg/thumbsUp.svg";
import ThumbsUpFilled from "@/helpers/svg/thumbsUpFilled.svg";
import { useSelector } from "react-redux";
import { Fade } from "@mui/material";
import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";
import { useRouter } from "next/navigation";
import PN from "persian-number";
import ResponsiveImage from "../elements/ResponsiveImage";
import EpisodeProgressIndicator from "./EpisodeProgressIndicator";
import { getPathname } from "@/helpers/browser.utils";
import {
  buildContentAnalyticsPayload,
  sendPlayIntent,
} from "@/services/mediaAnalyticsService";
import useSeriesWatchProgress from "@/hooks/useSeriesWatchProgress";
import ContentTag from "../content/tag";
import { getCMSImageURL } from "@/utils/cms.utils";
import { groupEpisodesBySeason, normalizeEpisodeTags } from "./episode.utils";
import { canPlayEpisode, isComingSoon } from "@/utils/contentStatus";

const EpisodeMetadata = ({ imdbRate, duration, isMobile = false }) => {
  return (
    <>
      {imdbRate && (
        <div className="series-episode-meta-item flex flex-row justify-center items-center z-10">
          <span className="font-semibold text-xs sm:text-sm md:text-base ml-2">
            {imdbRate}
          </span>
          <ImdbIcon />
        </div>
      )}
      {duration !== null && duration !== undefined && duration !== "" && (
        <div className="series-episode-meta-item flex flex-row justify-center items-center z-10">
          <span
            className={`font-semibold text-xs sm:text-sm md:text-base ml-2 ${
              isMobile ? "sm:-ml-1" : ""
            }`}
          >
            {duration} دقیقه
          </span>
          <ClockIcon />
        </div>
      )}
    </>
  );
};

const EpisodeContentActions = ({
  episodeNumber,
  seasonNumber,
  masterLoading,
  masterActionData,
  contentKey,
}) => {
  const { profile } = useSelector((state) => state.Auth);
  const [actionsLoading, setActionsLoading] = useState({
    like: false,
    dislike: false,
  });

  const [actionData, setActionData] = useState(masterActionData);

  useEffect(() => {
    setActionData(masterActionData);
  }, [masterActionData]);

  const userActionItem = useMemo(() => {
    const item = actionData?.userActions?.likeOrDislike?.find(
      (x) => x.season == seasonNumber && x.episode == episodeNumber
    );

    return item;
  }, [actionData, episodeNumber, seasonNumber]);

  const handleLike = useCallback(
    async ({ action }) => {
      if (actionsLoading.like || actionsLoading.dislike) return;
      if (action === "like" && userActionItem?.likeOrDislike === 1) return;
      if (action === "dislike" && userActionItem?.likeOrDislike === 2) return;

      setActionsLoading((prev) => ({
        ...prev,
        [action]: true,
      }));
      try {
        let response;
        if (action === "like") {
          response = await axiosInstance.post(getCMSApiRoute("action/like"), {
            contentKey,
            isEpisode: 1,
            episode: episodeNumber,
            season: seasonNumber,
          });

          if (response.status === 201) {
            const { data } = response.data;

            if (data.actionType && data.actionType === 1) {
              setActionData({
                ...actionData,
                userActions: {
                  ...actionData?.userActions,
                  likeOrDislike: [
                    {
                      likeOrDislike: 1,
                      episode: episodeNumber,
                      season: seasonNumber,
                    },
                  ],
                },
              });
            }
          }
        } else if (action === "dislike") {
          response = await axiosInstance.post(
            getCMSApiRoute("action/dislike"),
            {
              contentKey,
              isEpisode: 1,
              episode: episodeNumber,
              season: seasonNumber,
            }
          );

          if (response.status === 201) {
            const { data } = response.data;
            if (data.actionType && data.actionType === 2) {
              setActionData({
                ...actionData,
                userActions: {
                  ...actionData?.userActions,
                  likeOrDislike: [
                    {
                      episode: episodeNumber,
                      season: seasonNumber,
                      likeOrDislike: 2,
                    },
                  ],
                },
              });
            }
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        setActionsLoading((prev) => ({
          ...prev,
          [action]: false,
        }));
      }
    },
    [
      actionsLoading,
      userActionItem,
      contentKey,
      episodeNumber,
      seasonNumber,
      actionData,
    ]
  );

  return (
    <Fade in={profile.isLogin && !masterLoading} timeout={300}>
      <div className="series-episode-actions flex justify-end gap-2 z-10">
        <Button
          isIconOnly
          variant="light"
          className="series-episode-reaction"
          aria-label="نپسندیدن این قسمت"
          onPress={() =>
            handleLike({
              action: "dislike",
            })
          }
          isLoading={actionsLoading.dislike}
        >
          {userActionItem?.likeOrDislike === 2 ? (
            <ThumbsDownFilled />
          ) : (
            <ThumbsDown />
          )}
        </Button>
        <Button
          isIconOnly
          isLoading={actionsLoading.like}
          variant="light"
          className="series-episode-reaction"
          aria-label="پسندیدن این قسمت"
          onPress={() =>
            handleLike({
              action: "like",
            })
          }
        >
          {userActionItem?.likeOrDislike === 1 ? (
            <ThumbsUpFilled />
          ) : (
            <ThumbsUp />
          )}
        </Button>
      </div>
    </Fade>
  );
};

const SerialEpisode = ({ detail, data, canPlay, canPlayLoading }) => {
  const prepareAutoResume = useContentAutoResume(data?.slug);
  const [masterLoading, setMasterLoading] = useState(true);
  const [actionData, setActionData] = useState({});
  const { isLogin } = useSelector((state) => state.Auth?.profile);
  const { getEpisodeProgress } = useSeriesWatchProgress(data?.slug);
  const router = useRouter();

  const getContentActions = useCallback(
    async (signal) => {
      if (!isLogin || !data?.slug) {
        setActionData({});
        setMasterLoading(false);
        return;
      }

      setMasterLoading(true);
      try {
        const response = await axiosInstance.get(
          getCMSApiRoute("action/content"),
          {
            params: {
              contentKey: data.slug,
            },
            signal,
          }
        );

        if (response.status === 200) {
          const { data } = response.data;
          setActionData(data);
        }
      } catch (error) {
        if (error?.code !== "ERR_CANCELED") {
          console.log(error);
        }
      } finally {
        if (!signal?.aborted) {
          setMasterLoading(false);
        }
      }
    },
    [data?.slug, isLogin]
  );

  useEffect(() => {
    const controller = new AbortController();
    getContentActions(controller.signal);

    return () => controller.abort();
  }, [getContentActions]);

  const getPlayUrl = useCallback(
    (item) => {
      return (
        ROUTES.player +
        "/" +
        encodeURIComponent(data?.slug) +
        `?e=${item.episodeNumber}&s=${item.seasonNumber}`
      );
    },
    [data?.slug]
  );

  const seasons = useMemo(() => groupEpisodesBySeason(detail), [detail]);

  const reportContentWatch = useCallback(
    async (part) => {
      try {
        await axiosInstance.post(getCMSApiRoute("action/watch"), {
          contentKey: data?.slug,
          isEpisode: 1,
          season: part.seasonNumber,
          episode: part.episodeNumber,
        });
      } catch (error) {
        console.log(error);
      }
    },
    [data?.slug]
  );

  const reportPlayIntent = useCallback(
    (part) => {
      const payload = buildContentAnalyticsPayload(data, {
        surface: "content_detail",
        source: "episode_play_button",
        season: part.seasonNumber,
        episode: part.episodeNumber,
      });
      if (payload) sendPlayIntent(payload);
    },
    [data]
  );

  const handleWatchEvent = useCallback(
    async (part) => {
      if (!canPlayEpisode(data, part)) return;
      try {
        if (!isLogin) {
          const redirectUrl =
            data.isFree === 1 ? getPlayUrl(part) : getPathname();
          return router.push(
            `/auth/login?ref=${encodeURIComponent(redirectUrl)}`
          );
        }

        if (data.isFree !== 1 && !canPlay) {
          return router.push("/subscription");
        }

        await reportContentWatch(part);
        reportPlayIntent(part);
        const playUrl = getPlayUrl(part);
        prepareAutoResume(part.seasonNumber, part.episodeNumber);
        router.push(playUrl);
      } catch (error) {
        console.log(error);
      }
    },
    [
      isLogin,
      data.isFree,
      data.status,
      canPlay,
      router,
      getPlayUrl,
      reportContentWatch,
      reportPlayIntent,
      prepareAutoResume,
    ]
  );

  const getWatchLabel = useCallback(() => {
    if (!isLogin) return "ورود و تماشا";
    if (data?.isFree === 1 || canPlay) return "تماشا کن";
    return "خرید اشتراک و تماشا";
  }, [isLogin, data?.isFree, canPlay]);

  if (seasons.length === 0) return null;

  return (
    <section className="series-episode-section px-2">
      <Tabs
        className="series-season-tabs"
        variant="light"
        fullWidth
        destroyInactiveTabPanel={false}
        disableCursorAnimation
        classNames={{
          tabList: "series-season-tabs-list",
          cursor: "w-full bg-transparent",
          tab: "series-season-tab",
          tabContent: "series-season-tab-content",
          panel: "series-season-panel",
        }}
      >
        {seasons.map((season) => {
          return (
            <Tab
              key={`season_${season.seasonNumber}`}
              title={
                <div className="series-season-tab-label">
                  <span>
                    {season.seasonName ||
                      `فصل ${PN.convert(season.seasonNumber)}`}
                  </span>
                </div>
              }
            >
              {season.episodes.map((part) => {
                const comingSoon =
                  isComingSoon(data?.status) || isComingSoon(part.status);
                const playableStatus = canPlayEpisode(data, part);
                const watchProgress = getEpisodeProgress(
                  part.seasonNumber,
                  part.episodeNumber
                );
                const episodeTags = normalizeEpisodeTags(part.tags).filter(
                  (tag) => tag !== "بزودی"
                );

                return (
                  <div
                    key={`season_${part.seasonNumber}_ep_${part.episodeNumber}`}
                    className="series-episode-card"
                  >
                    <div className="series-episode-cover relative aspect-video w-full min-w-full shrink-0 overflow-hidden md:w-[310px] md:min-w-[310px]">
                      <ResponsiveImage
                        contentPhotos={part.data?.meta?.contentPhotos}
                        imageType="episodeCover"
                        alt=""
                        className="series-episode-artwork h-full w-full object-cover"
                        fallbackSrc={
                          getCMSImageURL(
                            part.data?.poster || part.data?.thumbnail
                          ) || "/placeholder.jpg"
                        }
                      />
                      {comingSoon ? (
                        <ContentTag
                          label="بزودی"
                          tags={["بزودی"]}
                          type="episode"
                        />
                      ) : episodeTags.length > 0 && (
                        <ContentTag
                          label={episodeTags[0]}
                          tags={episodeTags}
                          type="episode"
                        />
                      )}
                      {playableStatus && (
                        <EpisodeProgressIndicator progress={watchProgress} />
                      )}
                    </div>
                    <div className="series-episode-card-body">
                      <div className="series-episode-copy text-primary-white">
                        <h4 className="series-episode-title">
                          قسمت‌ {part.episodeNumber}
                        </h4>
                        {part?.data?.meta?.episodeDescription && (
                          <p className="series-episode-description">
                            {part?.data?.meta?.episodeDescription}
                          </p>
                        )}
                        {playableStatus ? (
                          <CustomButton
                            label={getWatchLabel()}
                            color="primary"
                            classNames="series-episode-watch-button flex text-primary-white md:w-fit"
                            variant="solid"
                            rightIcon={<WatchPlayIcon />}
                            onClick={() => handleWatchEvent(part)}
                            isLoading={canPlayLoading}
                            showLabelOnLoading={false}
                          />
                        ) : (
                          <span className="series-episode-watch-button inline-flex min-h-11 items-center rounded-full border border-white/25 bg-white/10 px-5 text-sm font-semibold text-white">
                            {comingSoon ? "بزودی" : "فعلاً قابل تماشا نیست"}
                          </span>
                        )}
                      </div>
                      <div className="series-episode-card-aside">
                        <div className="series-episode-metadata hidden sm:flex">
                          <EpisodeMetadata
                            imdbRate={part.data?.imdb_rate}
                            duration={part.data?.time}
                          />
                        </div>
                        <div className="series-episode-mobile-meta-wrap">
                          <div className="series-episode-metadata flex sm:hidden">
                            <EpisodeMetadata
                              imdbRate={part.data?.imdb_rate}
                              duration={part.data?.time}
                              isMobile={true}
                            />
                          </div>
                        </div>
                        {playableStatus && (
                          <EpisodeContentActions
                            seasonNumber={part.seasonNumber}
                            episodeNumber={part.episodeNumber}
                            masterLoading={masterLoading}
                            masterActionData={actionData}
                            contentKey={data?.slug}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </Tab>
          );
        })}
      </Tabs>
    </section>
  );
};

export default SerialEpisode;
